import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { Menu, X, Sun, Moon, Home, Layers, Sparkles, Users, MessageSquareQuote } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useTheme } from "@/context/ThemeContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const SECTION_IDS = ["home", "platforms", "features", "demographics", "testimonials"];

const iconMap = {
  home: Home,
  platforms: Layers,
  features: Sparkles,
  demographics: Users,
  testimonials: MessageSquareQuote,
};

export const Navbar = () => {
  const { t, toggleLang, lang } = useLang();
  const { theme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("home");
  const [open, setOpen] = useState(false);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    restDelta: 0.001,
  });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  // Lock body scroll + Esc-to-close when drawer open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const links = [
    { id: "home", label: t.nav.home },
    { id: "platforms", label: t.nav.platforms },
    { id: "features", label: t.nav.features },
    { id: "demographics", label: t.nav.demographics },
    { id: "testimonials", label: t.nav.testimonials },
  ];

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollTo(id), 180);
  };

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/85 dark:bg-slate-950/85 backdrop-blur-xl border-b border-slate-200/70 dark:border-slate-800/70 shadow-sm"
            : "bg-white/50 dark:bg-slate-950/50 backdrop-blur-md"
        }`}
      >
        <div dir="ltr" className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3 relative">
          {/* Hamburger — physical top-left */}
          <button
            data-testid="menu-toggle"
            aria-label="Open menu"
            aria-expanded={open}
            onClick={() => setOpen(true)}
            className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/70 text-slate-900 dark:text-slate-100 hover:border-cyan-400 hover:text-cyan-500 transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Brand — centered */}
          <div className="absolute left-1/2 -translate-x-1/2 pointer-events-none">
            <span className="font-heading font-black text-sm sm:text-base tracking-tight bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-600 bg-clip-text text-transparent">
              {t.hero.name}
            </span>
          </div>

          {/* Right controls */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid="theme-toggle"
              onClick={toggleTheme}
              aria-label="Toggle theme"
              className="relative inline-flex items-center justify-center h-10 w-10 rounded-lg border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/70 text-slate-900 dark:text-slate-100 hover:border-cyan-400 hover:text-cyan-500 transition-colors overflow-hidden"
            >
              <AnimatePresence mode="wait" initial={false}>
                {theme === "dark" ? (
                  <motion.span
                    key="sun"
                    initial={{ y: 12, opacity: 0, rotate: -90 }}
                    animate={{ y: 0, opacity: 1, rotate: 0 }}
                    exit={{ y: -12, opacity: 0, rotate: 90 }}
                    transition={{ duration: 0.2 }}
                    className="absolute"
                  >
                    <Sun className="h-5 w-5 text-yellow-400" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="moon"
                    initial={{ y: 12, opacity: 0, rotate: 90 }}
                    animate={{ y: 0, opacity: 1, rotate: 0 }}
                    exit={{ y: -12, opacity: 0, rotate: -90 }}
                    transition={{ duration: 0.2 }}
                    className="absolute"
                  >
                    <Moon className="h-5 w-5" />
                  </motion.span>
                )}
              </AnimatePresence>
            </button>

            <button
              data-testid="lang-toggle"
              onClick={toggleLang}
              className="inline-flex items-center gap-1.5 px-3 h-10 text-sm font-bold text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/70 rounded-lg hover:border-cyan-400 hover:text-cyan-500 transition-colors"
            >
              <span className="text-base leading-none">
                {lang === "ar" ? "🇬🇧" : "🇸🇦"}
              </span>
              {t.langLabel}
            </button>
          </div>
        </div>

        <motion.div
          style={{ scaleX }}
          className="h-0.5 origin-[left] bg-gradient-to-r from-blue-600 via-cyan-500 to-yellow-400"
        />
      </motion.nav>

      {/* Slide-in drawer from the physical left */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              data-testid="menu-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[60] bg-slate-950/50 backdrop-blur-sm"
            />
            <motion.aside
              data-testid="mobile-menu"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 34 }}
              className="fixed top-0 left-0 bottom-0 z-[70] w-[86%] max-w-xs bg-white dark:bg-slate-950 border-e border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col"
              dir={lang === "ar" ? "rtl" : "ltr"}
            >
              <div className="h-14 px-4 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
                <span className="font-heading font-black text-sm bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-600 bg-clip-text text-transparent">
                  {t.hero.name}
                </span>
                <button
                  data-testid="menu-close"
                  aria-label="Close menu"
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto p-3">
                <ul className="space-y-1">
                  {links.map((l, i) => {
                    const Icon = iconMap[l.id];
                    const isActive = active === l.id;
                    return (
                      <motion.li
                        key={l.id}
                        initial={{ opacity: 0, x: lang === "ar" ? 20 : -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.05 + i * 0.04 }}
                      >
                        <button
                          data-testid={`nav-link-${l.id}`}
                          onClick={() => go(l.id)}
                          className={`w-full flex items-center gap-3 rounded-xl px-3 py-3 text-start font-bold text-sm transition-all ${
                            isActive
                              ? "bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg shadow-cyan-500/20"
                              : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-900"
                          }`}
                        >
                          <span
                            className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${
                              isActive
                                ? "bg-white/20 text-white"
                                : "bg-slate-100 dark:bg-slate-900 text-cyan-600 dark:text-cyan-400"
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="flex-1">{l.label}</span>
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              isActive ? "bg-white" : "bg-transparent"
                            }`}
                          />
                        </button>
                      </motion.li>
                    );
                  })}
                </ul>
              </nav>

              <div className="p-4 border-t border-slate-200 dark:border-slate-800 text-xs text-slate-500 dark:text-slate-500">
                {t.footer.rights}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
